import Box from "@mui/material/Box";
import Grid from "@mui/material/Grid2";
import Button from "@mui/material/Button";
import Paper from "@mui/material/Paper";
import Typography from "@mui/material/Typography";
import Chip from "@mui/material/Chip";
import Stack from "@mui/material/Stack";
import { alpha, useTheme } from "@mui/material/styles";
import TuneIcon from "@mui/icons-material/Tune";
import PlayArrow from "@mui/icons-material/PlayArrow";
import Stop from "@mui/icons-material/Stop";
import ShowChart from "@mui/icons-material/ShowChart";
import ScheduleIcon from "@mui/icons-material/Schedule";
import Alert from "@mui/material/Alert";
import Snackbar from "@mui/material/Snackbar";
import IconButton from "@mui/material/IconButton";
import CloseIcon from "@mui/icons-material/Close";
import { useEffect, useRef, useState } from "react";
import { useResponsive } from "../../core/hooks/useResponsive";
import { useSocket } from "../../core/hooks/useSocket";
import useCookie from "../../core/hooks/useCookie";
import { IForm } from "../../core/form/IForm";
import { useTradeFormConfig } from "./useTradeFormConfig";
import { TradeStatus } from "../../config/constants";
import { ICore } from "../../core/ICore";
import TradeConfiguration from "./components/TradeConfiguration";
import { useDataFetch } from "../../core/hooks/useDataFetch";
import { Config } from "../../config/config";
import SystemError from "../SystemError/systemError";
import TradeLogGrid from "./components/TradeLogGrid";
import TradeBoughtGrid from "./components/TradeBoughtGrid";
import TradingSummaryPanel from "./components/TradingSummaryPanel";
import TradingDeskCharts from "./components/TradingDeskCharts";
import OrderHistoryGrid from "./components/OrderHistoryGrid";

const statusChip = (status) => {
  switch (status) {
    case TradeStatus.Running:
      return { label: "Running", color: "success" };
    case TradeStatus.Completed:
      return { label: "Completed", color: "info" };
    case TradeStatus.ReadyToStart:
      return { label: "Ready", color: "default" };
    case TradeStatus.Stopped:
      return { label: "Stopped", color: "warning" };
    case TradeStatus.OutsideWindow:
      return { label: "Market closed", color: "warning" };
    default:
      return { label: String(status || "—"), color: "default" };
  }
};

const Trading = () => {
  const theme = useTheme();
  const primary = theme.palette.primary.main;
  const { isPhone, isCompact } = useResponsive();
  const { startTrade, stopTrade, feedData, connect } = useSocket("buyProcess");
  const { callAPI } = useDataFetch();
  const { getToken } = useCookie();
  const [tradeCheck, setTradeCheck] = useState(null);
  const [notice, setNotice] = useState(null);
  const [marketShut, setMarketShut] = useState(false);
  const [autoOpenConfig, setAutoOpenConfig] = useState(false);
  // The advanced config is offered once per visit, not after every run.
  const configOfferedRef = useRef(false);

  const { tradeForm, populateValues } = useTradeFormConfig();

  const preTradeCheck = async () => {
    const res = await callAPI(Config.ITradeAPI.preTradeCheck);
    const data = res?.data;
    setTradeCheck(data ?? null);
    const shut = data?.tradeStatus === TradeStatus.OutsideWindow;
    setMarketShut(shut);
    // Instant cannot be started while the market is shut, so Scheduled is the only useful
    // choice - preselect it rather than leaving the user on a dead option.
    if (shut) tradeForm.setFieldValue("runProcessType", "Scheduled");
    // First load only. preTradeCheck also runs after a stop, and the popover remounts whenever
    // the run status leaves Running, so without this guard it would reopen every cycle.
    if (shut && !configOfferedRef.current) {
      configOfferedRef.current = true;
      setAutoOpenConfig(true);
    }
    // Populate whenever the API sent a budget, not only when ready to start: outside the
    // trading window the desk still renders and the form has to be usable to schedule a run.
    if (data?.tradeConfig?.budgetConfig) {
      populateValues(data.tradeConfig.budgetConfig);
    }
  };

  const handleStartTrade = async () => {
    const v = tradeForm.values;
    const payload = {
      algoKey: v.algoKey,
      statergyType: v.statergyType,
      tradeBudget: Number(v.tradeBudget),
      budgetPerTrade: Number(v.budgetPerTrade),
      tradeType: v.tradeType,
      runProcessType: v.runProcessType || "Instant",
      scheduleHour: Number(v.scheduleHour) ?? 0,
      scheduleMin: Number(v.scheduleMin) ?? 0,
      stopProfit: Number(v.stopProfit),
      stopLoss: Number(v.stopLoss),
      scanInterval: Number(v.scanInterval) || 30000,
    };
    const result = await startTrade(payload);
    const data = result?.data;
    // A rejected start is not a broken screen: surface it and leave the desk up so the user can
    // change the run type or the budget and try again.
    if (result?.error) {
      setNotice({ severity: "error", message: result.error || "Start trade failed" });
      return;
    }
    if (data?.status) {
      setTradeCheck((prev) => (prev ? { ...prev, tradeStatus: TradeStatus.Running } : { tradeStatus: TradeStatus.Running }));
    } else {
      setNotice({
        severity: "error",
        message: data?.errorMessage || data?.errorMsg || "Start trade failed",
      });
    }
  };

  const handleStopTrade = async () => {
    const result = await stopTrade();
    const data = result?.data;
    if (result?.error) {
      setNotice({ severity: "error", message: result.error || "Stop trade failed" });
      return;
    }
    if (data?.status) {
      setTradeCheck((prev) => (prev ? { ...prev, tradeStatus: TradeStatus.ReadyToStart } : { tradeStatus: TradeStatus.ReadyToStart }));
      preTradeCheck();
    } else {
      setNotice({
        severity: "error",
        message: data?.errorMessage || data?.errorMsg || "Stop trade failed",
      });
    }
  };

  useEffect(() => {
    const profileID = getToken()?.profileID;
    if (profileID) connect(profileID);
    preTradeCheck();
  }, []);

  // Transient notice rather than a standing banner - the amber "Market closed" chip in the
  // header is what carries the state once this has gone.
  useEffect(() => {
    if (tradeCheck?.tradeStatus === TradeStatus.OutsideWindow) {
      setNotice({ severity: "warning", message: null });
    }
  }, [tradeCheck?.tradeStatus]);

  useEffect(() => {
    if (feedData?.tradeStatus) {
      setTradeCheck((prev) => ({
        ...prev,
        tradeStatus: feedData.tradeStatus,
        message: feedData.message || "",
      }));
      if (feedData.tradeStatus === TradeStatus.Completed) {
        setNotice({ severity: "info", message: feedData.message || "Trading stopped." });
      }
    }
  }, [feedData?.tradeStatus]);

  if (tradeCheck === null) {
    return <div />;
  }

  if (tradeCheck.tradeStatus === TradeStatus.Error) {
    return (
      <SystemError
        title="Trade Precondition Failed"
        errorMessage={tradeCheck?.message}
      />
    );
  }

  const chip = statusChip(tradeCheck.tradeStatus);
  const live = tradeCheck.tradeStatus === TradeStatus.Running || tradeCheck.tradeStatus === TradeStatus.Completed;
  // Market shut: the desk is fully usable, but an Instant run cannot be kicked off - only a
  // scheduled one. The Start button follows the run type the user has selected.
  const isScheduled = tradeForm.values.runProcessType === "Scheduled";
  const isRunning = tradeCheck.tradeStatus === TradeStatus.Running;
  const startBlocked = isRunning || (marketShut && !isScheduled);
  // Only a Running engine can be stopped. Completed means it already stopped and cleaned up,
  // so stopTrade would just answer "trade not running for user X".
  const stopBlocked = !isRunning;

  return (
    <Box
      sx={{
        minHeight: "100%",
        bgcolor: alpha(primary, 0.02),
        pb: 1.5,
        pt: { xs: 0.75, md: 1.25 },
        px: { xs: 0.75, sm: 1.25, md: 1.5 },
      }}
    >
      <Box sx={{ maxWidth: 1280, mx: "auto" }}>
        <Stack direction="row" alignItems="center" justifyContent="space-between" flexWrap="wrap" gap={1} sx={{ mb: 1.25 }}>
          <Stack direction="row" alignItems="center" spacing={1}>
            <ShowChart sx={{ fontSize: isPhone ? 22 : 28, color: primary }} />
            <Typography variant={isPhone ? "h6" : "h5"} fontWeight={800} letterSpacing="-0.02em" noWrap>
              Buy engine
            </Typography>
            <Chip size="small" label={chip.label} color={chip.color} variant={chip.color === "default" ? "outlined" : "filled"} />
          </Stack>
        </Stack>

        <Paper
          elevation={0}
          sx={{
            borderRadius: 2,
            overflow: "hidden",
            border: `1px solid ${alpha(primary, 0.14)}`,
            boxShadow: `0 8px 24px ${alpha("#000", 0.04)}`,
            mb: 1.5,
          }}
        >
          <Box sx={{ px: 1.5, py: 1, bgcolor: alpha(primary, 0.06), borderBottom: `1px solid ${alpha(primary, 0.1)}` }}>
            <Typography variant="overline" fontWeight={800} color="primary" sx={{ lineHeight: 1.2 }}>
              Session setup
            </Typography>
          </Box>
          <IForm form={tradeForm}>
            <Grid container>
              <Grid
                size={{ xs: 12, md: 4 }}
                sx={{
                  p: { xs: 1.25, md: 1.5 },
                  borderBottom: { xs: `1px solid ${alpha(primary, 0.1)}`, md: "none" },
                  borderRight: { md: `1px solid ${alpha(primary, 0.1)}` },
                }}
              >
                <Typography variant="caption" color="text.secondary" fontWeight={700} sx={{ display: "block", mb: 1 }}>
                  Universe
                </Typography>
                <Typography variant="caption" color="text.secondary" sx={{ display: "block", mb: 1 }}>
                  Full exchange universe (Partner — no hot lists)
                </Typography>
                <Stack spacing={1.25}>
                  {/* BuyAlgorithms = this app's algorithms + IN.Core TrendData algorithms the buy engine can run (e.g. SR). */}
                  <ICore.ISelectBox model="algoKey" label="Algorithm" lookup="BuyAlgorithms" />
                </Stack>
              </Grid>
              <Grid
                size={{ xs: 12, md: 4 }}
                sx={{
                  p: { xs: 1.25, md: 1.5 },
                  borderBottom: { xs: `1px solid ${alpha(primary, 0.1)}`, md: "none" },
                  borderRight: { md: `1px solid ${alpha(primary, 0.1)}` },
                }}
              >
                <Typography variant="caption" color="text.secondary" fontWeight={700} sx={{ display: "block", mb: 1 }}>
                  Budget
                </Typography>
                <Grid container spacing={1.25}>
                  <Grid size={{ xs: 12, sm: 6 }}>
                    <ICore.ITextBox label="Trade budget" model="tradeBudget" />
                  </Grid>
                  <Grid size={{ xs: 12, sm: 6 }}>
                    <ICore.ITextBox label="Per trade" model="budgetPerTrade" />
                  </Grid>
                </Grid>
              </Grid>
              <Grid size={{ xs: 12, md: 4 }} sx={{ p: { xs: 1.25, md: 1.5 } }}>
                <Typography variant="caption" color="text.secondary" fontWeight={700} sx={{ display: "block", mb: 1 }}>
                  Control
                </Typography>
                <Stack spacing={1} direction="row" flexWrap="wrap" useFlexGap>
                  <Button
                    variant="contained"
                    color="primary"
                    startIcon={<PlayArrow />}
                    onClick={handleStartTrade}
                    disabled={startBlocked}
                    sx={{ fontWeight: 700 }}
                  >
                    Start
                  </Button>
                  <Button
                    variant="outlined"
                    color="primary"
                    startIcon={<Stop />}
                    onClick={handleStopTrade}
                    disabled={stopBlocked}
                    sx={{ fontWeight: 700 }}
                  >
                    Stop
                  </Button>
                  {tradeCheck.tradeStatus !== TradeStatus.Running && (
                    <ICore.IPopover icon={<TuneIcon />} title="Advanced config" width={500} autoOpen={autoOpenConfig} onAutoOpen={() => setAutoOpenConfig(false)}>
                      <TradeConfiguration />
                    </ICore.IPopover>
                  )}
                </Stack>
              </Grid>
            </Grid>
          </IForm>
        </Paper>

        {live && (
          <Stack spacing={1.5}>
            <Paper
              elevation={0}
              sx={{
                borderRadius: 2,
                p: { xs: 1, md: 1.5 },
                border: `1px solid ${alpha(primary, 0.12)}`,
                boxShadow: `0 6px 20px ${alpha("#000", 0.03)}`,
              }}
            >
              <Typography variant="overline" fontWeight={800} color="primary" sx={{ display: "block", mb: 1 }}>
                Live overview
              </Typography>

              <Grid container spacing={{ xs: 1, md: 1.5 }}>
                {/* Left column (50%): Process log + Summary */}
                <Grid size={{ xs: 12, lg: 6 }}>
                  <Stack
                    spacing={1.5}
                    sx={{
                      height: { xs: "auto", lg: 620 },
                      minHeight: { lg: 620 },
                    }}
                  >
                    <Paper
                      elevation={0}
                      sx={{
                        borderRadius: 2,
                        p: { xs: 1, md: 1.5 },
                        border: `1px solid ${alpha(primary, 0.12)}`,
                        flex: 1,
                        overflow: { xs: "visible", lg: "hidden" },
                        minHeight: { xs: "auto", lg: 0 },
                      }}
                    >
                      <TradeLogGrid tradeLog={feedData?.tradeLog} />
                    </Paper>
                    <Paper
                      elevation={0}
                      sx={{
                        borderRadius: 2,
                        p: { xs: 1, md: 1.5 },
                        border: `1px solid ${alpha(primary, 0.12)}`,
                        flex: 1,
                        overflow: { xs: "visible", lg: "hidden" },
                        minHeight: { xs: "auto", lg: 0 },
                      }}
                    >
                      <TradingSummaryPanel
                        data={feedData?.tradeSummary}
                        boughtList={feedData?.boughtList}
                        showCharts={false}
                      />
                    </Paper>
                  </Stack>
                </Grid>

                {/* Right column (50%): Graphs + Order grid */}
                <Grid size={{ xs: 12, lg: 6 }}>
                  <Stack
                    spacing={1.5}
                    sx={{
                      height: { xs: "auto", lg: 620 },
                      minHeight: { lg: 620 },
                    }}
                  >
                    <Paper
                      elevation={0}
                      sx={{
                        borderRadius: 2,
                        p: { xs: 1, md: 1.5 },
                        border: `1px solid ${alpha(primary, 0.12)}`,
                        flex: 1,
                        overflow: { xs: "visible", lg: "hidden" },
                        minHeight: { xs: "auto", lg: 0 },
                      }}
                    >
                      <TradingDeskCharts summary={feedData?.tradeSummary} boughtList={feedData?.boughtList} />
                    </Paper>
                    <Paper
                      elevation={0}
                      sx={{
                        borderRadius: 2,
                        p: { xs: 1, md: 1.5 },
                        border: `1px solid ${alpha(primary, 0.12)}`,
                        flex: 1,
                        overflow: { xs: "visible", lg: "hidden" },
                        minHeight: { xs: "auto", lg: 0 },
                      }}
                    >
                      <OrderHistoryGrid orderHistory={feedData?.orderHistory} />
                    </Paper>
                  </Stack>
                </Grid>

                {/* Last row: Open positions */}
                <Grid size={{ xs: 12 }}>
                  <Paper
                    elevation={0}
                    sx={{
                      mt: 0.5,
                      borderRadius: 2,
                      p: { xs: 1, md: 1.5 },
                      border: `1px solid ${alpha(primary, 0.12)}`,
                    }}
                  >
                    <TradeBoughtGrid boughtList={feedData?.boughtList} />
                  </Paper>
                </Grid>
              </Grid>
            </Paper>
          </Stack>
        )}

        {!live && (
          <Paper
            elevation={0}
            sx={{
              p: 2,
              textAlign: "center",
              borderRadius: 2,
              border: `1px dashed ${alpha(primary, 0.25)}`,
            }}
          >
            <Typography variant="subtitle1" fontWeight={700} color="text.secondary">
              Start the engine to see live summary, charts, log, and positions.
            </Typography>
          </Paper>
        )}
      </Box>

      <Snackbar
        open={Boolean(notice)}
        autoHideDuration={notice?.severity === "error" ? 10000 : 6000}
        onClose={() => setNotice(null)}
        anchorOrigin={{ vertical: "top", horizontal: "center" }}
      >
        <Alert
          severity={notice?.severity || "info"}
          icon={notice?.severity === "warning" ? <ScheduleIcon /> : undefined}
          variant="filled"
          action={
            <IconButton size="small" color="inherit" aria-label="Dismiss" onClick={() => setNotice(null)}>
              <CloseIcon fontSize="small" />
            </IconButton>
          }
          sx={{ borderRadius: 2, maxWidth: 560, alignItems: "center" }}
        >
          {notice?.message || (
            <>
              {tradeCheck?.message || "It is not the trading window - schedule the run instead."}
              {!isScheduled && ' Set Run Process Type to "Scheduled" to queue it.'}
            </>
          )}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default Trading;
